import { create } from 'zustand'

/**
 * Whether the assistant dock is open, and nothing else.
 *
 * A store rather than state in `AppShell` because the shell is mounted *per screen* — every
 * route renders its own `<AppShell>` — so shell-local state would close the chat on every
 * navigation. That is exactly the behaviour the rework exists to remove: the assistant is
 * supposed to follow the user around the app, not be a thing each page happens to offer.
 *
 * Deliberately **not** persisted. Which character is attached is server state and survives a
 * reload on its own; whether a panel is open is not worth restoring, and a chat that reopens
 * itself over the sheet on every launch is a nuisance.
 *
 * `openWith` is how the rest of the app hands the assistant a subject — the character sheet's
 * "Ask Bob about this character" does it. The dock reads it once, attaches the character and
 * clears it, so a later plain `open()` does not re-attach whatever was asked for last time.
 */
interface AssistantDock {
  isOpen: boolean
  /** A character the opener wants attached, consumed by the dock on open. */
  requestedCharacterId: number | null
  open: () => void
  openWith: (characterId: number) => void
  close: () => void
  toggle: () => void
  /** Called by the dock once it has acted on `requestedCharacterId`. */
  clearRequest: () => void
}

export const useAssistantDock = create<AssistantDock>((set) => ({
  isOpen: false,
  requestedCharacterId: null,
  open: () => set({ isOpen: true }),
  openWith: (characterId) => set({ isOpen: true, requestedCharacterId: characterId }),
  close: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  clearRequest: () => set({ requestedCharacterId: null }),
}))
